import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Col, FormFeedback, Input, Label, Row } from "reactstrap";

import { FeatureScheduleDto } from "../../interfaces/control-feature";
import {
  AvailabilityMode,
  DAYS_OF_WEEK,
  PerDayRow,
  buildFeatureSchedulePayload,
  defaultDaySchedules,
  defaultFeatureSchedule,
  perDayRowsFromSchedule,
  resolveAvailabilityMode,
  validateFeatureSchedule,
} from "../../utils/featureScheduleUtils";
import { getBrowserTimezone } from "../../utils/timezoneUtils";
import { TimezoneSelect } from "./TimezoneSelect";

export type FeatureScheduleEditorRef = {
  buildPayload: () => FeatureScheduleDto;
  validate: () => string | null;
};

type TProps = {
  schedule: FeatureScheduleDto;
  error?: string | null;
};

export const FeatureScheduleEditor = forwardRef<
  FeatureScheduleEditorRef,
  TProps
>(({ schedule, error }, ref) => {
  const { t } = useTranslation();
  const scheduleSnapshot = useMemo(() => JSON.stringify(schedule), [schedule]);

  const [availabilityMode, setAvailabilityMode] = useState<AvailabilityMode>(
    () => resolveAvailabilityMode(schedule),
  );
  const [perDayRows, setPerDayRows] = useState<PerDayRow[]>(() =>
    perDayRowsFromSchedule(schedule),
  );
  const [localAvailability, setLocalAvailability] = useState<FeatureScheduleDto>(
    () => ({ ...defaultFeatureSchedule(), ...schedule }),
  );

  useEffect(() => {
    const merged = { ...defaultFeatureSchedule(), ...schedule };
    setLocalAvailability(merged);
    setAvailabilityMode(resolveAvailabilityMode(merged));
    setPerDayRows(perDayRowsFromSchedule(merged));
  }, [scheduleSnapshot]);

  useImperativeHandle(
    ref,
    () => ({
      buildPayload: () =>
        buildFeatureSchedulePayload({
          availabilityMode,
          schedule: localAvailability,
          perDayRows,
        }),
      validate: () =>
        validateFeatureSchedule(
          availabilityMode,
          localAvailability,
          perDayRows,
        ),
    }),
    [availabilityMode, localAvailability, perDayRows],
  );

  const updateAvailability = (patch: Partial<FeatureScheduleDto>) => {
    setLocalAvailability((current) => ({ ...current, ...patch }));
  };

  const setMode = (mode: AvailabilityMode) => {
    setAvailabilityMode(mode);
    if (mode === "all_day") {
      setLocalAvailability((current) => ({
        ...current,
        allDay: true,
        startTime: "",
        endTime: "",
        daySchedules: undefined,
      }));
      return;
    }

    if (mode === "same_time") {
      setLocalAvailability((current) => ({
        ...current,
        allDay: false,
        daySchedules: undefined,
      }));
      return;
    }

    setPerDayRows(defaultDaySchedules());
    setLocalAvailability((current) => ({
      ...current,
      allDay: true,
      startTime: "",
      endTime: "",
      daySchedules: undefined,
    }));
  };

  return (
    <div>
      <Label htmlFor="scheduleTimezone" className="form-label">
        {t("scheduleTimezone")}
        <span className="text-danger">*</span>
      </Label>
      <TimezoneSelect
        value={localAvailability.timezone || getBrowserTimezone()}
        preferredTimezones={schedule.timezone ? [schedule.timezone] : []}
        onChange={(timezone) => updateAvailability({ timezone })}
      />
      <small className="text-muted d-block mb-3">
        {t("scheduleTimezoneHint")}
      </small>

      <Label className="form-label">{t("availability")}</Label>
      <div className="mb-2">
        <div className="form-check form-check-inline">
          <Input
            type="radio"
            id="scheduleAllDay"
            name="scheduleAvailabilityType"
            className="form-check-input"
            checked={availabilityMode === "all_day"}
            onChange={() => setMode("all_day")}
          />
          <Label className="form-check-label" htmlFor="scheduleAllDay">
            {t("allDay")}
          </Label>
        </div>
        <div className="form-check form-check-inline">
          <Input
            type="radio"
            id="scheduleTimeRange"
            name="scheduleAvailabilityType"
            className="form-check-input"
            checked={availabilityMode === "same_time"}
            onChange={() => setMode("same_time")}
          />
          <Label className="form-check-label" htmlFor="scheduleTimeRange">
            {t("specificTime")}
          </Label>
        </div>
        <div className="form-check form-check-inline">
          <Input
            type="radio"
            id="schedulePerDay"
            name="scheduleAvailabilityType"
            className="form-check-input"
            checked={availabilityMode === "per_day"}
            onChange={() => setMode("per_day")}
          />
          <Label className="form-check-label" htmlFor="schedulePerDay">
            {t("timeForEachDay")}
          </Label>
        </div>
      </div>

      {availabilityMode === "per_day" && (
        <div className="table-responsive mb-2">
          <table className="table table-sm table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>{t("day")}</th>
                <th className="text-center" style={{ width: 100 }}>
                  {t("available")}
                </th>
                <th className="text-center" style={{ width: 90 }}>
                  {t("allDay")}
                </th>
                <th>{t("startTime")}</th>
                <th>{t("endTime")}</th>
              </tr>
            </thead>
            <tbody>
              {DAYS_OF_WEEK.map((day) => {
                const row = perDayRows[day.value];
                if (!row) return null;
                return (
                  <tr key={day.value}>
                    <td>{t(day.labelKey)}</td>
                    <td className="text-center">
                      <Input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) => {
                          setPerDayRows((rows) =>
                            rows.map((item) =>
                              item.dayOfWeek === day.value
                                ? { ...item, enabled: e.target.checked }
                                : item,
                            ),
                          );
                        }}
                      />
                    </td>
                    <td className="text-center">
                      <Input
                        type="checkbox"
                        checked={row.allDay}
                        disabled={!row.enabled}
                        onChange={(e) => {
                          setPerDayRows((rows) =>
                            rows.map((item) =>
                              item.dayOfWeek === day.value
                                ? {
                                    ...item,
                                    allDay: e.target.checked,
                                    startTime: e.target.checked
                                      ? ""
                                      : item.startTime,
                                    endTime: e.target.checked
                                      ? ""
                                      : item.endTime,
                                  }
                                : item,
                            ),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        type="time"
                        className="form-control form-control-sm"
                        value={row.startTime}
                        disabled={!row.enabled || row.allDay}
                        onChange={(e) => {
                          setPerDayRows((rows) =>
                            rows.map((item) =>
                              item.dayOfWeek === day.value
                                ? { ...item, startTime: e.target.value }
                                : item,
                            ),
                          );
                        }}
                      />
                    </td>
                    <td>
                      <Input
                        type="time"
                        className="form-control form-control-sm"
                        value={row.endTime}
                        disabled={!row.enabled || row.allDay}
                        onChange={(e) => {
                          setPerDayRows((rows) =>
                            rows.map((item) =>
                              item.dayOfWeek === day.value
                                ? { ...item, endTime: e.target.value }
                                : item,
                            ),
                          );
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <small className="text-muted">{t("timeForEachDayHint")}</small>
        </div>
      )}

      {availabilityMode === "same_time" && !localAvailability.allDay && (
        <Row className="g-2 mb-2">
          <Col sm={6}>
            <Label htmlFor="scheduleStartTime" className="form-label">
              {t("startTime")}
              <span className="text-danger">*</span>
            </Label>
            <Input
              id="scheduleStartTime"
              type="time"
              value={localAvailability.startTime || ""}
              onChange={(e) => updateAvailability({ startTime: e.target.value })}
            />
          </Col>
          <Col sm={6}>
            <Label htmlFor="scheduleEndTime" className="form-label">
              {t("endTime")}
              <span className="text-danger">*</span>
            </Label>
            <Input
              id="scheduleEndTime"
              type="time"
              value={localAvailability.endTime || ""}
              onChange={(e) => updateAvailability({ endTime: e.target.value })}
            />
          </Col>
        </Row>
      )}

      {availabilityMode !== "per_day" && (
        <>
          <Label className="form-label mt-2">{t("daysOfWeek")}</Label>
          <div className="d-flex flex-wrap gap-2">
            {DAYS_OF_WEEK.map((day) => (
              <div className="form-check form-check-inline" key={day.value}>
                <Input
                  type="checkbox"
                  id={`schedule-day-${day.value}`}
                  className="form-check-input"
                  checked={(localAvailability.daysOfWeek || []).includes(
                    day.value,
                  )}
                  onChange={(e) => {
                    const current = localAvailability.daysOfWeek || [];
                    const next = e.target.checked
                      ? [...current, day.value]
                      : current.filter((value: number) => value !== day.value);
                    updateAvailability({ daysOfWeek: next });
                  }}
                />
                <Label
                  className="form-check-label"
                  htmlFor={`schedule-day-${day.value}`}
                >
                  {t(day.labelKey)}
                </Label>
              </div>
            ))}
          </div>
          <small className="text-muted">{t("leaveEmptyForAllDays")}</small>
        </>
      )}

      {error ? (
        <FormFeedback type="invalid" className="d-block mt-2">
          {t(error)}
        </FormFeedback>
      ) : null}
    </div>
  );
});

FeatureScheduleEditor.displayName = "FeatureScheduleEditor";
